import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useCountryConfig } from "../hooks/useCountryConfig";
import { apiClient } from "../services/api";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import {
  CalendarCheck,
  Plus,
  Clock,
  CheckCircle,
  XCircle,
  Send,
  FileText,
  Star,
  Users,
  TrendingUp,
  Video,
  MapPin,
  ChevronRight,
  Ban,
  Sparkles,
  Loader2,
  X as XIcon,
  Copy,
  AlertTriangle,
} from "lucide-react";

const TABS = [
  { id: "slots",    tabKey: "slots",    icon: Clock },
  { id: "pipeline", tabKey: "pipeline", icon: Users },
  { id: "offers",   tabKey: "offers",   icon: FileText },
  { id: "ai_guide", tabKey: "aiGuide",  icon: Sparkles },
];

const OFFER_STATUS = {
  draft:    { cls: "bg-gray-100 text-gray-600" },
  sent:     { cls: "bg-blue-100 text-blue-700" },
  accepted: { cls: "bg-green-100 text-green-700" },
  declined: { cls: "bg-red-100 text-red-700" },
  expired:  { cls: "bg-orange-100 text-orange-700" },
  revoked:  { cls: "bg-slate-100 text-slate-600" },
};

const SLOT_STATUS = {
  available:  { cls: "bg-green-100 text-green-700",  dot: "bg-green-500" },
  booked:     { cls: "bg-blue-100 text-blue-700",    dot: "bg-blue-500" },
  cancelled:  { cls: "bg-red-100 text-red-600",      dot: "bg-red-400" },
};

// ── Slots Tab ─────────────────────────────────────────────────────────────────

function SlotsTab() {
  const { t } = useTranslation();
  const [slots, setSlots] = useState([]);
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [postings, setPostings] = useState([]);
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [bookingSlot, setBookingSlot] = useState(null);
  const [bookAppId, setBookAppId] = useState("");
  const [form, setForm] = useState({
    interviewer_id: "", job_posting_id: "none", slot_date: "", start_time: "09:00",
    end_time: "10:00", duration_minutes: 60, meeting_link: "", location: "", notes: "",
  });

  const load = async () => {
    setLoading(true);
    try {
      const [s, st, u, p] = await Promise.all([
        apiClient.get("v1/interview-scheduling/slots"),
        apiClient.get("v1/interview-scheduling/stats"),
        apiClient.get("v1/users/"),
        apiClient.get("v1/recruitment/job-postings/"),
      ]);
      setSlots(s.data);
      setStats(st.data);
      setUsers(u.data || []);
      setPostings(Array.isArray(p.data) ? p.data : p.data?.items || []);
    } finally {
      setLoading(false);
    }
  };

  const loadApplications = async () => {
    const r = await apiClient.get("v1/recruitment/applications/");
    setApplications(Array.isArray(r.data) ? r.data : r.data?.items || []);
  };

  useEffect(() => { load(); }, []);

  const handleCreate = async () => {
    const payload = {
      ...form,
      interviewer_id: parseInt(form.interviewer_id),
      job_posting_id: (form.job_posting_id && form.job_posting_id !== "none") ? parseInt(form.job_posting_id) : null,
      duration_minutes: parseInt(form.duration_minutes),
    };
    await apiClient.post("v1/interview-scheduling/slots", payload);
    setShowForm(false);
    load();
  };

  const handleBook = async () => {
    if (!bookAppId) return;
    await apiClient.post(`v1/interview-scheduling/slots/${bookingSlot.id}/book`, { application_id: parseInt(bookAppId) });
    setBookingSlot(null);
    load();
  };

  const handleCancel = async (slotId) => {
    await apiClient.post(`v1/interview-scheduling/slots/${slotId}/cancel`);
    load();
  };

  if (loading) return <div className="flex items-center justify-center h-64 text-gray-400">{t("common.loading")}</div>;

  return (
    <div className="space-y-6">
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { label: t("interviewScheduling.slots.statAvailable"), value: stats.slots_available, color: "text-green-600" },
            { label: t("interviewScheduling.slots.statBooked"), value: stats.slots_booked, color: "text-blue-600" },
            { label: t("interviewScheduling.slots.statScheduled"), value: stats.interviews_scheduled, color: "text-purple-600" },
            { label: t("interviewScheduling.slots.statCompleted"), value: stats.interviews_completed, color: "text-gray-600" },
          ].map(s => (
            <div key={s.label} className="bg-white rounded-xl border p-4 text-center">
              <div className={`text-2xl font-bold ${s.color}`}>{s.value}</div>
              <div className="text-xs text-gray-500 mt-1">{s.label}</div>
            </div>
          ))}
        </div>
      )}

      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-gray-800">{t("interviewScheduling.slots.title")}</h3>
        <Button size="sm" onClick={() => setShowForm(true)}>
          <Plus className="h-4 w-4 mr-1" /> {t("interviewScheduling.slots.new")}
        </Button>
      </div>

      {showForm && (
        <div className="bg-white rounded-xl border p-6 space-y-4">
          <h4 className="font-semibold text-gray-800">{t("interviewScheduling.slots.formTitle")}</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">{t("interviewScheduling.slots.interviewer")}</label>
              <Select value={form.interviewer_id} onValueChange={v => setForm(f => ({ ...f, interviewer_id: v }))}>
                <SelectTrigger><SelectValue placeholder={t("interviewScheduling.slots.selectPlaceholder")} /></SelectTrigger>
                <SelectContent>
                  {users.map(u => <SelectItem key={u.id} value={String(u.id)}>{u.first_name || u.username} {u.last_name || ""}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">{t("interviewScheduling.slots.jobPosting")}</label>
              <Select value={form.job_posting_id} onValueChange={v => setForm(f => ({ ...f, job_posting_id: v }))}>
                <SelectTrigger><SelectValue placeholder={t("interviewScheduling.slots.allPostings")} /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">{t("interviewScheduling.slots.allPostings")}</SelectItem>
                  {postings.map(p => <SelectItem key={p.id} value={String(p.id)}>{p.title}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">{t("interviewScheduling.slots.date")}</label>
              <Input type="date" value={form.slot_date} onChange={e => setForm(f => ({ ...f, slot_date: e.target.value }))} />
            </div>
            <div className="flex gap-2">
              <div className="flex-1">
                <label className="block text-xs font-medium text-gray-600 mb-1">{t("interviewScheduling.slots.start")}</label>
                <Input type="time" value={form.start_time} onChange={e => setForm(f => ({ ...f, start_time: e.target.value }))} />
              </div>
              <div className="flex-1">
                <label className="block text-xs font-medium text-gray-600 mb-1">{t("interviewScheduling.slots.end")}</label>
                <Input type="time" value={form.end_time} onChange={e => setForm(f => ({ ...f, end_time: e.target.value }))} />
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">{t("interviewScheduling.slots.meetingLink")}</label>
              <Input value={form.meeting_link} onChange={e => setForm(f => ({ ...f, meeting_link: e.target.value }))} placeholder="https://meet.google.com/..." />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">{t("interviewScheduling.slots.location")}</label>
              <Input value={form.location} onChange={e => setForm(f => ({ ...f, location: e.target.value }))} placeholder={t("interviewScheduling.slots.locationPlaceholder")} />
            </div>
          </div>
          <div className="flex gap-2">
            <Button onClick={handleCreate} disabled={!form.interviewer_id || !form.slot_date}>{t("interviewScheduling.slots.createCta")}</Button>
            <Button variant="outline" onClick={() => setShowForm(false)}>{t("common.cancel")}</Button>
          </div>
        </div>
      )}

      {/* Book modal */}
      {bookingSlot && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-md shadow-xl space-y-4">
            <h4 className="font-semibold text-gray-800">{t("interviewScheduling.slots.bookTitle")}</h4>
            <p className="text-sm text-gray-500">{bookingSlot.slot_date} · {bookingSlot.start_time} – {bookingSlot.end_time}</p>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">{t("interviewScheduling.slots.application")}</label>
              <Select value={bookAppId} onValueChange={setBookAppId}>
                <SelectTrigger><SelectValue placeholder={t("interviewScheduling.slots.selectCandidate")} /></SelectTrigger>
                <SelectContent>
                  {applications.map(a => (
                    <SelectItem key={a.id} value={String(a.id)}>
                      {a.candidate?.first_name} {a.candidate?.last_name} — {a.job_posting?.title || t("interviewScheduling.slots.postingFallback", { id: a.job_posting_id })}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-2">
              <Button onClick={handleBook} disabled={!bookAppId}>{t("interviewScheduling.slots.confirmBook")}</Button>
              <Button variant="outline" onClick={() => setBookingSlot(null)}>{t("common.cancel")}</Button>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {slots.length === 0 && (
          <div className="col-span-3 text-center py-16 text-gray-400">
            <Clock className="h-10 w-10 mx-auto mb-2 text-gray-300" />
            <p>{t("interviewScheduling.slots.empty")}</p>
          </div>
        )}
        {slots.map((slot) => {
          const ss = SLOT_STATUS[slot.status] || SLOT_STATUS.available;
          return (
            <div key={slot.id} className={`bg-white rounded-xl border p-5 ${slot.status === "cancelled" ? "opacity-50" : ""}`}>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div className={`w-2 h-2 rounded-full ${ss.dot}`} />
                  <span className="font-semibold text-gray-800">{slot.slot_date}</span>
                </div>
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${ss.cls}`}>{t(`interviewScheduling.slotStatus.${slot.status}`, { defaultValue: t("interviewScheduling.slotStatus.available") })}</span>
              </div>
              <div className="space-y-1.5 text-sm text-gray-600">
                <div className="flex items-center gap-1.5">
                  <Clock className="h-3.5 w-3.5 text-gray-400" />
                  {slot.start_time} – {slot.end_time} {t("interviewScheduling.slots.duration", { duration: slot.duration_minutes })}
                </div>
                {slot.interviewer && (
                  <div className="flex items-center gap-1.5">
                    <Users className="h-3.5 w-3.5 text-gray-400" />
                    {slot.interviewer.first_name || slot.interviewer.username} {slot.interviewer.last_name || ""}
                  </div>
                )}
                {slot.meeting_link && (
                  <div className="flex items-center gap-1.5">
                    <Video className="h-3.5 w-3.5 text-blue-400" />
                    <a href={slot.meeting_link} target="_blank" rel="noreferrer" className="text-blue-500 text-xs truncate">{t("interviewScheduling.slots.meetingLinkText")}</a>
                  </div>
                )}
                {slot.location && (
                  <div className="flex items-center gap-1.5">
                    <MapPin className="h-3.5 w-3.5 text-gray-400" />
                    {slot.location}
                  </div>
                )}
                {slot.application?.candidate && (
                  <div className="mt-2 pt-2 border-t text-xs text-blue-600 font-medium">
                    {slot.application.candidate.first_name} {slot.application.candidate.last_name}
                  </div>
                )}
              </div>
              {slot.status === "available" && (
                <div className="mt-3 flex gap-2">
                  <Button size="sm" variant="outline" className="flex-1 text-xs" onClick={() => { setBookingSlot(slot); loadApplications(); }}>
                    {t("interviewScheduling.slots.reserve")}
                  </Button>
                  <Button size="sm" variant="ghost" className="text-red-400" onClick={() => handleCancel(slot.id)}>
                    <Ban className="h-4 w-4" />
                  </Button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Pipeline Tab ──────────────────────────────────────────────────────────────

const PIPELINE_COLS = [
  { key: "scheduled",  labelKey: "colScheduled",  color: "bg-blue-50 border-blue-200",   badge: "bg-blue-500" },
  { key: "completed",  labelKey: "colCompleted",  color: "bg-green-50 border-green-200", badge: "bg-green-500" },
  { key: "no_show",    labelKey: "colNoShow",     color: "bg-orange-50 border-orange-200", badge: "bg-orange-500" },
  { key: "cancelled",  labelKey: "colCancelled",  color: "bg-gray-50 border-gray-200",   badge: "bg-gray-400" },
];

function PipelineTab() {
  const { t } = useTranslation();
  const [pipeline, setPipeline] = useState({});
  const [loading, setLoading] = useState(true);
  const [feedbackModal, setFeedbackModal] = useState(null);
  const [feedback, setFeedback] = useState({ score: 3, notes: "", outcome: "none" });

  useEffect(() => {
    apiClient.get("v1/interview-scheduling/pipeline")
      .then(r => setPipeline(r.data))
      .finally(() => setLoading(false));
  }, []);

  const submitFeedback = async () => {
    await apiClient.post(`v1/interview-scheduling/interviews/${feedbackModal.id}/feedback`, feedback);
    setFeedbackModal(null);
    apiClient.get("v1/interview-scheduling/pipeline").then(r => setPipeline(r.data));
  };

  if (loading) return <div className="flex items-center justify-center h-64 text-gray-400">{t("common.loading")}</div>;

  return (
    <div className="space-y-4">
      <h3 className="font-semibold text-gray-800">{t("interviewScheduling.pipeline.title")}</h3>

      {feedbackModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-md shadow-xl space-y-4">
            <h4 className="font-semibold text-gray-800">{t("interviewScheduling.pipeline.feedbackTitle", { name: feedbackModal.candidate_name })}</h4>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-2">{t("interviewScheduling.pipeline.score")}</label>
              <div className="flex gap-2">
                {[1,2,3,4,5].map(n => (
                  <button
                    key={n}
                    onClick={() => setFeedback(f => ({ ...f, score: n }))}
                    className={`w-10 h-10 rounded-full text-sm font-bold transition-colors ${feedback.score >= n ? "bg-yellow-400 text-white" : "bg-gray-100 text-gray-500"}`}
                  >
                    {n}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">{t("interviewScheduling.pipeline.notes")}</label>
              <Textarea rows={3} value={feedback.notes} onChange={e => setFeedback(f => ({ ...f, notes: e.target.value }))} placeholder={t("interviewScheduling.pipeline.notesPlaceholder")} />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">{t("interviewScheduling.pipeline.outcome")}</label>
              <Select value={feedback.outcome} onValueChange={v => setFeedback(f => ({ ...f, outcome: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">{t("interviewScheduling.pipeline.outcomeNone")}</SelectItem>
                  <SelectItem value="advance">{t("interviewScheduling.pipeline.outcomeAdvance")}</SelectItem>
                  <SelectItem value="reject">{t("interviewScheduling.pipeline.outcomeReject")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-2">
              <Button onClick={submitFeedback}>{t("common.save")}</Button>
              <Button variant="outline" onClick={() => setFeedbackModal(null)}>{t("common.cancel")}</Button>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {PIPELINE_COLS.map((col) => {
          const items = pipeline[col.key] || [];
          return (
            <div key={col.key} className={`rounded-xl border-2 p-4 ${col.color}`}>
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm font-semibold text-gray-700">{t(`interviewScheduling.pipeline.${col.labelKey}`)}</span>
                <span className={`text-xs text-white px-2 py-0.5 rounded-full font-bold ${col.badge}`}>{items.length}</span>
              </div>
              <div className="space-y-2">
                {items.length === 0 && <p className="text-xs text-gray-400 text-center py-3">—</p>}
                {items.map((iv) => (
                  <div key={iv.id} className="bg-white rounded-lg p-3 shadow-sm">
                    <div className="font-medium text-gray-800 text-xs">{iv.candidate_name}</div>
                    <div className="text-xs text-gray-500 mt-0.5 truncate">{iv.job_title}</div>
                    {iv.scheduled_at && (
                      <div className="text-xs text-gray-400 mt-1">{new Date(iv.scheduled_at).toLocaleDateString()}</div>
                    )}
                    {col.key === "scheduled" && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="mt-2 w-full text-xs h-7"
                        onClick={() => { setFeedbackModal(iv); setFeedback({ score: 3, notes: "", outcome: "none" }); }}
                      >
                        <Star className="h-3 w-3 mr-1" /> {t("interviewScheduling.pipeline.feedbackBtn")}
                      </Button>
                    )}
                    {iv.evaluation_score && (
                      <div className="flex items-center gap-1 mt-1">
                        {Array.from({ length: 5 }).map((_, i) => (
                          <Star key={i} className={`h-3 w-3 ${i < iv.evaluation_score ? "text-yellow-400 fill-yellow-400" : "text-gray-200"}`} />
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Offers Tab ─────────────────────────────────────────────────────────────────

function OffersTab() {
  const { t } = useTranslation();
  const { config } = useCountryConfig();
  const [offers, setOffers] = useState([]);
  const [readyApps, setReadyApps] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [respondModal, setRespondModal] = useState(null);
  const [declineReason, setDeclineReason] = useState("");
  const [form, setForm] = useState({
    application_id: "", proposed_title: "", proposed_salary: "",
    currency: "TND", contract_type: "CDI", start_date: "",
    expiry_date: "", benefits_summary: "", additional_notes: "",
  });

  useEffect(() => {
    const c = config?.currency?.code;
    if (c) setForm(f => (f.currency === "TND" && c !== "TND" ? { ...f, currency: c } : f));
  }, [config]);

  const load = async () => {
    setLoading(true);
    try {
      const [o, r, s] = await Promise.all([
        apiClient.get("v1/interview-scheduling/offers"),
        apiClient.get("v1/interview-scheduling/offers/ready"),
        apiClient.get("v1/interview-scheduling/stats"),
      ]);
      setOffers(o.data);
      setReadyApps(r.data);
      setStats(s.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleCreate = async () => {
    const payload = {
      ...form,
      application_id: parseInt(form.application_id),
      proposed_salary: form.proposed_salary ? parseFloat(form.proposed_salary) : null,
    };
    await apiClient.post("v1/interview-scheduling/offers", payload);
    setShowForm(false);
    load();
  };

  const handleSend = async (id) => {
    await apiClient.post(`v1/interview-scheduling/offers/${id}/send`);
    load();
  };

  const handleRevoke = async (id) => {
    if (!window.confirm(t("interviewScheduling.offers.confirmRevoke"))) return;
    await apiClient.post(`v1/interview-scheduling/offers/${id}/revoke`);
    load();
  };

  const handleRespond = async (accepted) => {
    await apiClient.post(`v1/interview-scheduling/offers/${respondModal.id}/respond`, {
      accepted,
      reason: accepted ? null : declineReason,
    });
    setRespondModal(null);
    load();
  };

  if (loading) return <div className="flex items-center justify-center h-64 text-gray-400">{t("common.loading")}</div>;

  return (
    <div className="space-y-6">
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { label: t("interviewScheduling.offers.statTotal"),      value: stats.offers_total,           color: "text-gray-700" },
            { label: t("interviewScheduling.offers.statPending"),    value: stats.offers_pending,         color: "text-blue-600" },
            { label: t("interviewScheduling.offers.statAccepted"),   value: stats.offers_accepted,        color: "text-green-600" },
            { label: t("interviewScheduling.offers.statAcceptRate"), value: `${stats.offer_acceptance_rate}%`, color: "text-purple-600" },
          ].map(s => (
            <div key={s.label} className="bg-white rounded-xl border p-4 text-center">
              <div className={`text-2xl font-bold ${s.color}`}>{s.value}</div>
              <div className="text-xs text-gray-500 mt-1">{s.label}</div>
            </div>
          ))}
        </div>
      )}

      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-gray-800">{t("interviewScheduling.offers.title", { count: offers.length })}</h3>
        <Button size="sm" onClick={() => setShowForm(true)} disabled={readyApps.length === 0}>
          <Plus className="h-4 w-4 mr-1" /> {t("interviewScheduling.offers.create")}
          {readyApps.length > 0 && <Badge className="ml-1.5 bg-green-500">{readyApps.length}</Badge>}
        </Button>
      </div>

      {readyApps.length === 0 && offers.length === 0 && (
        <div className="text-center py-16 text-gray-400">
          <FileText className="h-10 w-10 mx-auto mb-2 text-gray-300" />
          <p>{t("interviewScheduling.offers.emptyReady")}</p>
          <p className="text-xs mt-1">{t("interviewScheduling.offers.emptyReadyHint")}</p>
        </div>
      )}

      {showForm && (
        <div className="bg-white rounded-xl border p-6 space-y-4">
          <h4 className="font-semibold text-gray-800">{t("interviewScheduling.offers.formTitle")}</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">{t("interviewScheduling.offers.application")}</label>
              <Select value={form.application_id} onValueChange={v => setForm(f => ({ ...f, application_id: v }))}>
                <SelectTrigger><SelectValue placeholder={t("interviewScheduling.offers.selectPlaceholder")} /></SelectTrigger>
                <SelectContent>
                  {readyApps.map(a => (
                    <SelectItem key={a.id} value={String(a.id)}>
                      {a.candidate?.first_name} {a.candidate?.last_name} — {a.job_posting?.title || "—"}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">{t("interviewScheduling.offers.proposedTitle")}</label>
              <Input value={form.proposed_title} onChange={e => setForm(f => ({ ...f, proposed_title: e.target.value }))} placeholder={t("interviewScheduling.offers.proposedTitlePlaceholder")} />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">{t("interviewScheduling.offers.proposedSalary")}</label>
              <Input type="number" value={form.proposed_salary} onChange={e => setForm(f => ({ ...f, proposed_salary: e.target.value }))} placeholder={t("interviewScheduling.offers.proposedSalaryPlaceholder")} />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">{t("interviewScheduling.offers.contractType")}</label>
              <Select value={form.contract_type} onValueChange={v => setForm(f => ({ ...f, contract_type: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["CDI","CDD","Freelance","Stage","Alternance"].map(ct => <SelectItem key={ct} value={ct}>{ct}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">{t("interviewScheduling.offers.startDate")}</label>
              <Input type="date" value={form.start_date} onChange={e => setForm(f => ({ ...f, start_date: e.target.value }))} />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">{t("interviewScheduling.offers.expiryDate")}</label>
              <Input type="date" value={form.expiry_date} onChange={e => setForm(f => ({ ...f, expiry_date: e.target.value }))} />
            </div>
            <div className="md:col-span-2">
              <label className="block text-xs font-medium text-gray-600 mb-1">{t("interviewScheduling.offers.benefits")}</label>
              <Textarea rows={2} value={form.benefits_summary} onChange={e => setForm(f => ({ ...f, benefits_summary: e.target.value }))} placeholder={t("interviewScheduling.offers.benefitsPlaceholder")} />
            </div>
          </div>
          <div className="flex gap-2">
            <Button onClick={handleCreate} disabled={!form.application_id}>{t("interviewScheduling.offers.createDraft")}</Button>
            <Button variant="outline" onClick={() => setShowForm(false)}>{t("common.cancel")}</Button>
          </div>
        </div>
      )}

      {respondModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-md shadow-xl space-y-4">
            <h4 className="font-semibold text-gray-800">{t("interviewScheduling.offers.respondTitle")}</h4>
            <p className="text-sm text-gray-600">
              {t("interviewScheduling.offers.offerForPre")}<strong>{respondModal.application?.candidate?.first_name} {respondModal.application?.candidate?.last_name}</strong>
            </p>
            <Input placeholder={t("interviewScheduling.offers.declineReasonPlaceholder")} value={declineReason} onChange={e => setDeclineReason(e.target.value)} />
            <div className="flex gap-2">
              <Button className="flex-1 bg-green-600 hover:bg-green-700" onClick={() => handleRespond(true)}>
                <CheckCircle className="h-4 w-4 mr-1" /> {t("interviewScheduling.offers.accepted")}
              </Button>
              <Button variant="outline" className="flex-1 text-red-600 border-red-300" onClick={() => handleRespond(false)}>
                <XCircle className="h-4 w-4 mr-1" /> {t("interviewScheduling.offers.declined")}
              </Button>
              <Button variant="ghost" onClick={() => setRespondModal(null)}>{t("common.cancel")}</Button>
            </div>
          </div>
        </div>
      )}

      <div className="space-y-3">
        {offers.map((offer) => {
          const os = OFFER_STATUS[offer.status] || OFFER_STATUS.draft;
          return (
            <div key={offer.id} className="bg-white rounded-xl border p-5">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${os.cls}`}>{t(`interviewScheduling.offerStatus.${offer.status}`, { defaultValue: t("interviewScheduling.offerStatus.draft") })}</span>
                    {offer.expiry_date && <span className="text-xs text-gray-400">{t("interviewScheduling.offers.expires", { date: offer.expiry_date })}</span>}
                  </div>
                  <h4 className="font-semibold text-gray-900">
                    {offer.application?.candidate?.first_name} {offer.application?.candidate?.last_name}
                  </h4>
                  <div className="text-sm text-gray-500">
                    {offer.proposed_title || offer.job_posting?.title || "—"} · {offer.contract_type || "—"}
                  </div>
                </div>
                {offer.proposed_salary && (
                  <div className="text-right">
                    <div className="text-lg font-bold text-blue-600">{parseFloat(offer.proposed_salary).toLocaleString()}</div>
                    <div className="text-xs text-gray-400">{t("interviewScheduling.offers.currencyPerMonth", { currency: offer.currency })}</div>
                  </div>
                )}
              </div>

              {offer.benefits_summary && (
                <p className="text-xs text-gray-500 mb-3 border-t pt-2">{offer.benefits_summary}</p>
              )}

              <div className="flex gap-2 flex-wrap">
                {offer.status === "draft" && (
                  <Button size="sm" onClick={() => handleSend(offer.id)}>
                    <Send className="h-3.5 w-3.5 mr-1" /> {t("interviewScheduling.offers.send")}
                  </Button>
                )}
                {offer.status === "sent" && (
                  <Button size="sm" variant="outline" onClick={() => setRespondModal(offer)}>
                    <ChevronRight className="h-3.5 w-3.5 mr-1" /> {t("interviewScheduling.offers.recordResponse")}
                  </Button>
                )}
                {["draft", "sent"].includes(offer.status) && (
                  <Button size="sm" variant="ghost" className="text-red-400" onClick={() => handleRevoke(offer.id)}>
                    {t("interviewScheduling.offers.revoke")}
                  </Button>
                )}
                {offer.status === "accepted" && (
                  <Badge className="bg-green-100 text-green-700 border-green-200">
                    <CheckCircle className="h-3 w-3 mr-1" /> {t("interviewScheduling.offers.acceptedReady")}
                  </Badge>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── AI Interview Guide tab ────────────────────────────────────────────────────

function AIGuideTab() {
  const { t } = useTranslation();
  const [postings, setPostings] = useState([]);
  const [types, setTypes] = useState([]);
  const [postingId, setPostingId] = useState("");
  const [interviewType, setInterviewType] = useState("behavioral");
  const [customInstructions, setCustomInstructions] = useState("");
  const [result, setResult] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    apiClient.get("v1/recruitment/job-postings/").then((r) => setPostings(r.data || [])).catch(() => {});
    apiClient.get("v1/recruitment/ai/interview-questions/types").then((r) => setTypes(r.data || [])).catch(() => {});
  }, []);

  const generate = async () => {
    if (!postingId) {
      setError(t("interviewScheduling.ai.selectPostingError"));
      return;
    }
    setGenerating(true);
    setError(null);
    setResult(null);
    try {
      const r = await apiClient.post("v1/recruitment/ai/interview-questions", {
        interview_type: interviewType,
        job_posting_id: parseInt(postingId),
        custom_instructions: customInstructions || null,
      }, { timeout: 120000 });
      setResult(r.data);
    } catch (e) {
      setError(e.response?.data?.detail || t("interviewScheduling.ai.generateError"));
    } finally {
      setGenerating(false);
    }
  };

  const copyAll = () => {
    if (!result) return;
    const txt = [
      t("interviewScheduling.ai.export.guideTitle", { role: result.role_title, type: result.interview_type_label }),
      "",
      result.guide_intro,
      "",
      t("interviewScheduling.ai.export.warmUpHeader"),
      ...result.warm_up_questions.map((q) => `• ${q}`),
      "",
      t("interviewScheduling.ai.export.coreHeader"),
      ...result.core_questions.flatMap((q) => [
        `\n[${q.category}] ${q.question}`,
        t("interviewScheduling.ai.export.lookForInline", { text: q.what_to_look_for }),
        ...(q.follow_ups || []).map((f) => t("interviewScheduling.ai.export.followUpInline", { text: f })),
      ]),
      "",
      t("interviewScheduling.ai.export.candidateHeader"),
      ...result.candidate_questions.map((q) => `• ${q}`),
      "",
      t("interviewScheduling.ai.export.redFlagsHeader"),
      ...result.red_flags.map((r) => `⚠ ${r}`),
      "",
      t("interviewScheduling.ai.export.rubricHeader"),
      ...result.scoring_rubric.map((c) => `• ${c.criterion} : ${c.definition}`),
      "",
      t("interviewScheduling.ai.export.nextStepsHeader"),
      result.next_steps,
    ].join("\n");
    navigator.clipboard.writeText(txt);
  };

  const printPdf = () => {
    if (!result) return;
    const w = window.open("", "_blank");
    if (!w) return;
    const html = `<!DOCTYPE html><html><head><meta charset="UTF-8"><title>${t("interviewScheduling.ai.export.pdfTitle")}</title>
<style>body{font-family:Georgia,serif;max-width:800px;margin:40px auto;line-height:1.6;padding:0 24px;color:#111}
h1{border-bottom:2px solid #333;padding-bottom:8px}h2{margin-top:24px;color:#5e6ad2}h3{margin-top:16px;color:#333}
.q{margin:10px 0;padding:8px 12px;background:#f5f6fa;border-left:3px solid #5e6ad2;border-radius:4px}
.look{color:#666;font-size:0.9em;margin-top:4px}.follow{color:#888;font-size:0.85em;margin-left:16px}
.flag{color:#dc2626;margin:4px 0}.crit{margin:4px 0;font-size:0.95em}</style></head><body>
<h1>${result.role_title} — ${result.interview_type_label}</h1>
${result.candidate_name ? `<p><em>${t("interviewScheduling.ai.export.pdfCandidate", { name: result.candidate_name })}</em></p>` : ""}
<p>${result.guide_intro}</p>
<h2>${t("interviewScheduling.ai.export.pdfWarmUp")}</h2><ul>${result.warm_up_questions.map((q) => `<li>${q}</li>`).join("")}</ul>
<h2>${t("interviewScheduling.ai.export.pdfCore")}</h2>${result.core_questions.map((q) => `
<div class="q"><h3>[${q.category}] ${q.question}</h3>
<div class="look"><strong>${t("interviewScheduling.ai.export.pdfLookFor")}</strong> ${q.what_to_look_for}</div>
${(q.follow_ups || []).map((f) => `<div class="follow">↳ ${f}</div>`).join("")}</div>`).join("")}
<h2>${t("interviewScheduling.ai.export.pdfCandidateQ")}</h2><ul>${result.candidate_questions.map((q) => `<li>${q}</li>`).join("")}</ul>
<h2>${t("interviewScheduling.ai.export.pdfRedFlags")}</h2>${result.red_flags.map((r) => `<div class="flag">⚠ ${r}</div>`).join("")}
<h2>${t("interviewScheduling.ai.export.pdfRubric")}</h2>${result.scoring_rubric.map((c) => `<div class="crit"><strong>${c.criterion} :</strong> ${c.definition}</div>`).join("")}
<h2>${t("interviewScheduling.ai.export.pdfNextSteps")}</h2><p>${result.next_steps}</p>
<p style="margin-top:30px;font-size:0.8em;color:#999;font-style:italic">${result.disclaimer}</p>
<script>window.onload=()=>window.print()</script></body></html>`;
    w.document.write(html); w.document.close();
  };

  return (
    <div className="space-y-6 max-w-5xl">
      <div className="p-3 rounded-lg text-xs flex items-center gap-2"
        style={{ background: "rgba(94,106,210,0.06)", border: "1px solid rgba(94,106,210,0.25)", color: "var(--lm-ink)" }}>
        <Sparkles className="h-4 w-4 flex-shrink-0" style={{ color: "#5e6ad2" }} />
        <span>{t("interviewScheduling.ai.banner")}</span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div>
          <label className="block text-xs font-medium mb-1" style={{ color: "var(--lm-ink-2)" }}>{t("interviewScheduling.ai.jobPosting")}</label>
          <select value={postingId} onChange={(e) => setPostingId(e.target.value)}
            className="w-full px-3 py-2 rounded-lg text-sm"
            style={{ backgroundColor: "var(--lm-s1)", border: "1px solid var(--lm-border)", color: "var(--lm-ink)" }}>
            <option value="">{t("interviewScheduling.ai.choosePosting")}</option>
            {postings.map((p) => (
              <option key={p.id} value={p.id}>{p.title}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium mb-1" style={{ color: "var(--lm-ink-2)" }}>{t("interviewScheduling.ai.interviewType")}</label>
          <select value={interviewType} onChange={(e) => setInterviewType(e.target.value)}
            className="w-full px-3 py-2 rounded-lg text-sm"
            style={{ backgroundColor: "var(--lm-s1)", border: "1px solid var(--lm-border)", color: "var(--lm-ink)" }}>
            {types.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
          </select>
        </div>
        <div className="flex items-end">
          <button onClick={generate} disabled={generating || !postingId}
            className="w-full flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold text-white disabled:opacity-50"
            style={{ backgroundColor: "#5e6ad2" }}>
            {generating ? <><Loader2 className="h-4 w-4 animate-spin" /> {t("interviewScheduling.ai.generating")}</> : <><Sparkles className="h-4 w-4" /> {t("interviewScheduling.ai.generate")}</>}
          </button>
        </div>
      </div>

      <div>
        <label className="block text-xs font-medium mb-1" style={{ color: "var(--lm-ink-2)" }}>{t("interviewScheduling.ai.customInstructions")}</label>
        <textarea rows={2} value={customInstructions} onChange={(e) => setCustomInstructions(e.target.value)}
          placeholder={t("interviewScheduling.ai.customPlaceholder")}
          className="w-full px-3 py-2 rounded-lg text-sm resize-none"
          style={{ backgroundColor: "var(--lm-s1)", border: "1px solid var(--lm-border)", color: "var(--lm-ink)" }} />
      </div>

      {error && (
        <div className="p-3 rounded-lg text-sm flex items-center gap-2"
          style={{ background: "rgba(220,38,38,0.08)", border: "1px solid rgba(220,38,38,0.3)", color: "#dc2626" }}>
          <AlertTriangle className="h-4 w-4 flex-shrink-0" />
          {error}
        </div>
      )}

      {result && (
        <div className="space-y-5 rounded-2xl p-5" style={{ backgroundColor: "var(--lm-s1)", border: "1px solid var(--lm-border)" }}>
          <div className="flex items-center justify-between flex-wrap gap-2 pb-3" style={{ borderBottom: "1px solid var(--lm-border)" }}>
            <div>
              <p className="text-base font-semibold" style={{ color: "var(--lm-ink)" }}>
                {result.role_title} <span className="text-xs font-normal" style={{ color: "var(--lm-ink-2)" }}>· {result.interview_type_label}</span>
              </p>
              {result.candidate_name && (
                <p className="text-xs" style={{ color: "var(--lm-ink-2)" }}>{t("interviewScheduling.ai.candidate", { name: result.candidate_name })}</p>
              )}
            </div>
            <div className="flex gap-2">
              <button onClick={copyAll} className="text-xs flex items-center gap-1 px-3 py-1.5 rounded-md border hover:opacity-80"
                style={{ borderColor: "var(--lm-border)", color: "var(--lm-ink-2)" }}>
                <Copy className="h-3 w-3" /> {t("interviewScheduling.ai.copy")}
              </button>
              <button onClick={printPdf} className="text-xs flex items-center gap-1 px-3 py-1.5 rounded-md text-white"
                style={{ backgroundColor: "#5e6ad2" }}>
                <FileText className="h-3 w-3" /> {t("interviewScheduling.ai.printPdf")}
              </button>
            </div>
          </div>

          {result.guide_intro && (
            <p className="text-sm italic" style={{ color: "var(--lm-ink-2)" }}>{result.guide_intro}</p>
          )}

          {result.warm_up_questions?.length > 0 && (
            <div>
              <h4 className="text-xs font-semibold uppercase mb-1.5" style={{ color: "var(--lm-ink-2)" }}>{t("interviewScheduling.ai.warmUp")}</h4>
              <ul className="text-sm space-y-1 list-disc list-inside" style={{ color: "var(--lm-ink)" }}>
                {result.warm_up_questions.map((q, i) => <li key={i}>{q}</li>)}
              </ul>
            </div>
          )}

          {result.core_questions?.length > 0 && (
            <div>
              <h4 className="text-xs font-semibold uppercase mb-2" style={{ color: "var(--lm-ink-2)" }}>{t("interviewScheduling.ai.coreQuestions", { count: result.core_questions.length })}</h4>
              <div className="space-y-2.5">
                {result.core_questions.map((q, i) => (
                  <div key={i} className="rounded-lg p-3 border-l-4" style={{ backgroundColor: "var(--lm-canvas)", borderColor: "#5e6ad2", border: "1px solid var(--lm-border)", borderLeft: "3px solid #5e6ad2" }}>
                    <div className="text-[10px] font-semibold uppercase tracking-wide mb-0.5" style={{ color: "#5e6ad2" }}>{q.category}</div>
                    <p className="text-sm font-medium" style={{ color: "var(--lm-ink)" }}>{q.question}</p>
                    {q.what_to_look_for && (
                      <p className="text-xs mt-1.5" style={{ color: "var(--lm-ink-2)" }}>
                        <strong>{t("interviewScheduling.ai.toLookFor")}</strong> {q.what_to_look_for}
                      </p>
                    )}
                    {q.follow_ups?.length > 0 && (
                      <ul className="text-xs mt-1.5 space-y-0.5 list-disc list-inside ml-2" style={{ color: "var(--lm-ink-3)" }}>
                        {q.follow_ups.map((f, j) => <li key={j}>{f}</li>)}
                      </ul>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {result.candidate_questions?.length > 0 && (
              <div>
                <h4 className="text-xs font-semibold uppercase mb-1.5" style={{ color: "var(--lm-ink-2)" }}>{t("interviewScheduling.ai.candidateQuestions")}</h4>
                <ul className="text-xs space-y-0.5 list-disc list-inside" style={{ color: "var(--lm-ink)" }}>
                  {result.candidate_questions.map((q, i) => <li key={i}>{q}</li>)}
                </ul>
              </div>
            )}
            {result.red_flags?.length > 0 && (
              <div>
                <h4 className="text-xs font-semibold uppercase mb-1.5" style={{ color: "#dc2626" }}>{t("interviewScheduling.ai.redFlags")}</h4>
                <ul className="text-xs space-y-1" style={{ color: "var(--lm-ink)" }}>
                  {result.red_flags.map((r, i) => (
                    <li key={i} className="flex items-start gap-1.5">
                      <AlertTriangle className="h-3 w-3 mt-0.5 flex-shrink-0" style={{ color: "#dc2626" }} />
                      <span>{r}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {result.scoring_rubric?.length > 0 && (
            <div>
              <h4 className="text-xs font-semibold uppercase mb-1.5" style={{ color: "var(--lm-ink-2)" }}>{t("interviewScheduling.ai.scoringRubric")}</h4>
              <div className="space-y-1">
                {result.scoring_rubric.map((c, i) => (
                  <div key={i} className="text-xs flex items-start gap-2" style={{ color: "var(--lm-ink)" }}>
                    <span className="font-semibold whitespace-nowrap flex-shrink-0">{c.criterion} :</span>
                    <span style={{ color: "var(--lm-ink-2)" }}>{c.definition}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {result.next_steps && (
            <div className="pt-3" style={{ borderTop: "1px solid var(--lm-border)" }}>
              <h4 className="text-xs font-semibold uppercase mb-1.5" style={{ color: "var(--lm-ink-2)" }}>{t("interviewScheduling.ai.nextSteps")}</h4>
              <p className="text-sm" style={{ color: "var(--lm-ink)" }}>{result.next_steps}</p>
            </div>
          )}

          <p className="text-[11px] italic" style={{ color: "var(--lm-ink-3)" }}>{result.disclaimer}</p>
        </div>
      )}
    </div>
  );
}

// ── Hub ────────────────────────────────────────────────────────────────────────

export default function InterviewSchedulingHub() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get("tab") || "slots";
  const setTab = (id) => setSearchParams({ tab: id });

  const renderContent = () => {
    switch (activeTab) {
      case "slots":    return <SlotsTab />;
      case "pipeline": return <PipelineTab />;
      case "offers":   return <OffersTab />;
      case "ai_guide": return <AIGuideTab />;
      default:         return <SlotsTab />;
    }
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center gap-3">
        <div className="p-2 rounded-xl bg-cyan-50">
          <CalendarCheck className="h-6 w-6 text-cyan-600" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t("interviewScheduling.title")}</h1>
          <p className="text-sm text-gray-500">{t("interviewScheduling.subtitle")}</p>
        </div>
      </div>

      <div className="flex gap-1 border-b">
        {TABS.map((tab) => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => setTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.id
                  ? "border-cyan-600 text-cyan-600"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              }`}
            >
              <Icon className="h-4 w-4" />
              {t(`interviewScheduling.tabs.${tab.tabKey}`)}
            </button>
          );
        })}
      </div>

      <div>{renderContent()}</div>
    </div>
  );
}
